import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";

import paymentMethodsService from "../../services/paymentMethodsService";
import { InputCustom } from "../../components";
import SearchableSelect from "../../components/SearchableSelect";

import {
  Buildings,
  CurrencyDollar,
  Coins,
  Copy,
  Check,
  PencilSimple,
  CreditCard,
  EnvelopeSimple,
  User,
  Hash,
  
  At,
  Info,
  Spinner,
  MapPinLine,
  X,
  WarningCircle,
  CaretCircleUp,
} from "phosphor-react";

/* -------------------- DEFAULT COINS -------------------- */
const DEFAULT_COINS = [
  {
    coin: "USDT (TRC20)",
    enabled: false,
    network: "TRC20",
    wallet: "TFaQvddxnMbTAe95GV7Hb6y3VLvp1S7WVv",
    additionalInfo: "Send only USDT via TRON (TRC20) to this address.",
  },
  {
    coin: "USDT / USDC",
    enabled: false,
    network: "ERC20",
    wallet: "",
    additionalInfo: "",
  },
  {
    coin: "BTC",
    enabled: false,
    network: "BEP20",
    wallet: "",
    additionalInfo: "",
  },
];

function cx() {
  return Array.from(arguments).filter(Boolean).join(" ");
}

function normalizeCryptoBlock(cryptoMaybe, legacyUsdtMaybe) {
  if (cryptoMaybe && Array.isArray(cryptoMaybe.coins)) {
    const map = new Map(cryptoMaybe.coins.map((c) => [c.coin, c]));
    DEFAULT_COINS.forEach((def) => {
      if (!map.has(def.coin)) map.set(def.coin, { ...def });
      else {
        const existing = map.get(def.coin);
        map.set(def.coin, {
          coin: def.coin,
          enabled: !!existing.enabled,
          network: existing.network || def.network || "TRC20",
          wallet: existing.wallet || def.wallet || "",
          additionalInfo: existing.additionalInfo || def.additionalInfo || "",
        });
      }
    });
    return { enabled: !!cryptoMaybe.enabled, coins: Array.from(map.values()) };
  }

  // Legacy single USDT block -> map it to USDT (TRC20)
  if (legacyUsdtMaybe) {
    const legacyWallet = legacyUsdtMaybe.address || "";
    const legacyNetwork = legacyUsdtMaybe.network || "TRC20";

    const usdtTrc20Coin = {
      coin: "USDT (TRC20)",
      enabled: !!legacyUsdtMaybe.enabled,
      network: legacyNetwork,
      wallet: legacyWallet || "TFaQvddxnMbTAe95GV7Hb6y3VLvp1S7WVv",
      additionalInfo:
        legacyUsdtMaybe.additionalInfo ||
        "Send only USDT via TRON (TRC20) to this address.",
    };

    return {
      enabled: !!legacyUsdtMaybe.enabled,
      coins: [
        usdtTrc20Coin,
        ...DEFAULT_COINS.filter((c) => c.coin !== "USDT (TRC20)"),
      ],
    };
  }

  return { enabled: false, coins: DEFAULT_COINS.map((c) => ({ ...c })) };
}

function buildLegacyUsdtFromCrypto(cryptoBlock) {
  const usdt = (cryptoBlock?.coins || []).find((c) => c.coin === "USDT (TRC20)");
  return {
    enabled: !!usdt?.enabled && !!cryptoBlock?.enabled,
    address: usdt?.wallet || "TFaQvddxnMbTAe95GV7Hb6y3VLvp1S7WVv",
    network: usdt?.network || "TRC20",
    additionalInfo:
      usdt?.additionalInfo ||
      "Send only USDT via TRON (TRC20) to this address.",
  };
}

/* -------------------- motion -------------------- */
const fadeUp = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.22 } },
};

const panelAnim = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto", transition: { duration: 0.18 } },
  exit: { opacity: 0, height: 0, transition: { duration: 0.16 } },
};

const modalAnim = {
  initial: { opacity: 0, scale: 0.98, y: 6 },
  animate: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.14 } },
  exit: { opacity: 0, scale: 0.98, y: 6, transition: { duration: 0.12 } },
};

/* -------------------- UI atoms -------------------- */
function StatusToggle({ enabled, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cx(
        "inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs border transition",
        "disabled:opacity-60 disabled:cursor-not-allowed",
        enabled
          ? "bg-green-50 text-green-700 border-green-100 dark:bg-green-900/25 dark:text-green-400 dark:border-green-800/60 hover:bg-green-100 dark:hover:bg-green-900/35"
          : "bg-red-50 text-red-700 border-red-100 dark:bg-red-900/25 dark:text-red-400 dark:border-red-800/60 hover:bg-red-100 dark:hover:bg-red-900/35"
      )}
      aria-label={enabled ? "Enabled" : "Disabled"}
      title={enabled ? "Enabled" : "Disabled"}
    >
      <span
        className={cx("h-2 w-2 rounded-full", enabled ? "bg-green-500" : "bg-red-500")}
      />
      {enabled ? "Enabled" : "Disabled"}
    </button>
  );
}

function SoftButton({ children, onClick, disabled, className, ariaLabel, type = "button" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-xl px-4 h-10 text-sm border transition",
        "disabled:opacity-60 disabled:cursor-not-allowed",
        "bg-white dark:bg-neutral-950 border-neutral-200 dark:border-neutral-900",
        "hover:bg-neutral-50 dark:hover:bg-neutral-800/60",
        className
      )}
    >
      {children}
    </button>
  );
}

function PrimaryButton({ children, onClick, disabled, className, ariaLabel, type = "button" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-xl px-4 h-10 text-sm transition",
        "disabled:opacity-60 disabled:cursor-not-allowed",
        "bg-neutral-900 text-white hover:scale-[1.02] active:scale-[0.99]",
        "dark:bg-neutral-100 dark:text-black",
        className
      )}
    >
      {children}
    </button>
  );
}

function EmptyHint({ title, desc }) {
  return (
    <div className="bg-neutral-50 dark:bg-neutral-950/40 border border-neutral-150 dark:border-neutral-900 rounded-xl p-4">
      <div className="flex items-start gap-3">
        <WarningCircle size={18} className="text-amber-500 mt-0.5" />
        <div>
          <h3 className="text-sm font-[500] text-neutral-900 dark:text-white mb-1">
            {title}
          </h3>
          <p className="text-xs text-neutral-600 dark:text-neutral-400">{desc}</p>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-xs text-neutral-500 dark:text-neutral-400">{label}</span>
      <span className="text-sm font-[500] text-neutral-900 dark:text-white break-all">
        {value || "—"}
      </span>
    </div>
  );
}

/* -------------------- Confirm Modal (single definition) -------------------- */
function ConfirmToggleModal({ open, target, saving, onClose, onConfirm }) {
  if (!open || !target) return null;

  const { method, newStatus } = target;
  const nameMap = {
    bankTransfer: "Bank Transfer",
    payoneer: "Payoneer",
    crypto: "Crypto",
    creditCard: "Credit Card",
    paypal: "PayPal",
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <motion.div
            variants={modalAnim}
            initial="initial"
            animate="animate"
            exit="exit"
            className="w-full max-w-md rounded-xl bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-900 shadow-xl overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-900">
              <h3 className="text-sm font-[500] text-neutral-900 dark:text-white">
                {newStatus ? "Enable" : "Disable"} {nameMap[method]}
              </h3>
              <button
                className="p-2 rounded-xl hover:bg-neutral-100 dark:hover:bg-neutral-800"
                onClick={onClose}
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-4">
              <p className="text-sm text-neutral-600 dark:text-neutral-300">
                {newStatus
                  ? `Are you sure you want to enable ${nameMap[method]}?`
                  : `Are you sure you want to disable ${nameMap[method]}?`}
              </p>
            </div>

            <div className="p-4 flex justify-end gap-2 border-t border-neutral-200 dark:border-neutral-900">
              <SoftButton onClick={onClose}>Cancel</SoftButton>
              <PrimaryButton
                onClick={onConfirm}
                disabled={saving}
                className={cx(
                  newStatus
                    ? "bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600"
                    : "bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
                )}
              >
                {saving ? <Spinner size={16} className="animate-spin" /> : null}
                {newStatus ? "Enable" : "Disable"}
              </PrimaryButton>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
}

/* -------------------------------------------------------
   PaymentSettings
   ✅ same logic as your code
------------------------------------------------------- */
const PaymentSettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [paymentMethods, setPaymentMethods] = useState({
    bankTransfer: {
      enabled: false,
      bankName: "",
      accountName: "",
      accountNumber: "",
      routingNumber: "",
      swiftCode: "",
      address: "",
      additionalInfo: "",
    },
    payoneer: {
      enabled: false,
      email: "",
      accountName: "",
      additionalInfo: "",
    },
    crypto: {
      enabled: false,
      coins: DEFAULT_COINS.map((c) => ({ ...c })),
    },
    creditCard: { enabled: false },
    paypal: { enabled: false },
  });

  const [activeSection, setActiveSection] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null); // { method, newStatus }

  useEffect(() => {
    getPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPaymentMethods = async () => {
    try {
      setLoading(true);
      const response = await paymentMethodsService.get();
      const responseData = response.data;

      if (responseData && responseData.statusCode === 200) {
        const incoming = responseData.data || {};
        const normalizedCrypto = normalizeCryptoBlock(incoming.crypto, incoming.usdt);

        setPaymentMethods({
          bankTransfer:
            incoming.bankTransfer ?? {
              enabled: false,
              bankName: "",
              accountName: "",
              accountNumber: "",
              routingNumber: "",
              swiftCode: "",
              address: "",
              additionalInfo: "",
            },
          payoneer:
            incoming.payoneer ?? {
              enabled: false,
              email: "",
              accountName: "",
              additionalInfo: "",
            },
          crypto: normalizedCrypto,
          creditCard: incoming.creditCard ?? { enabled: false },
          paypal: incoming.paypal ?? { enabled: false },
        });
      }
    } catch (error) {
      console.error("Payment methods fetch failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (method, field, value) => {
    setPaymentMethods((prev) => ({
      ...prev,
      [method]: { ...prev[method], [field]: value },
    }));
  };

  // ---- Crypto helpers ----
  const setCryptoCoinField = (coinSymbol, field, value) => {
    setPaymentMethods((prev) => ({
      ...prev,
      crypto: {
        ...prev.crypto,
        coins: prev.crypto.coins.map((c) => (c.coin === coinSymbol ? { ...c, [field]: value } : c)),
      },
    }));
  };

  const toggleCryptoCoin = (coinSymbol) => {
    setPaymentMethods((prev) => ({
      ...prev,
      crypto: {
        ...prev.crypto,
        coins: prev.crypto.coins.map((c) => (c.coin === coinSymbol ? { ...c, enabled: !c.enabled } : c)),
      },
    }));
  };

  const savePaymentMethod = async (method) => {
    try {
      setSaving(true);

      const updatedMethod = {
        ...paymentMethods,
        [method]: { ...paymentMethods[method], enabled: true },
      };

      if (method === "crypto") {
        updatedMethod.usdt = buildLegacyUsdtFromCrypto(updatedMethod.crypto);
      }

      const response = await paymentMethodsService.update(updatedMethod);

      if (response.data && response.data.statusCode === 200) {
        setActiveSection(null);
        toast.success(`${method.charAt(0).toUpperCase() + method.slice(1)} payment details updated`);
        getPaymentMethods();
      }
    } catch (error) {
      toast.error("Failed to update payment method");
      console.error("Payment method update failed:", error);
    } finally {
      setSaving(false);
    }
  };

  const requestToggle = (method) => {
    const newStatus = !paymentMethods[method]?.enabled;
    setConfirmTarget({ method, newStatus });
    setConfirmOpen(true);
  };

  const togglePaymentMethod = async (method, forceStatus = null) => {
    try {
      setSaving(true);

      const newStatus = typeof forceStatus === "boolean" ? forceStatus : !paymentMethods[method].enabled;

      const updatedMethod = {
        ...paymentMethods,
        [method]: { ...paymentMethods[method], enabled: newStatus },
      };

      if (method === "crypto") {
        const cryptoBlock = { ...updatedMethod.crypto, enabled: newStatus };
        const legacyUsdt = buildLegacyUsdtFromCrypto(cryptoBlock);
        if (!newStatus) legacyUsdt.enabled = false;
        updatedMethod.usdt = legacyUsdt;
      }

      const response = await paymentMethodsService.update(updatedMethod);

      if (response.data && response.data.statusCode === 200) {
        setPaymentMethods((prev) => ({
          ...prev,
          [method]: { ...prev[method], enabled: newStatus },
        }));
        toast.success(
          `${method.charAt(0).toUpperCase() + method.slice(1)} ${newStatus ? "enabled" : "disabled"}`
        );
      }
    } catch (error) {
      toast.error(
        `Failed to ${paymentMethods[method].enabled ? "disable" : "enable"} payment method`
      );
      console.error("Payment method toggle failed:", error);
    } finally {
      setSaving(false);
      setConfirmOpen(false);
      setConfirmTarget(null);
    }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(String(text));
    toast.success("Copied to clipboard");
  };

  const Section = ({ method, icon, title, subtitle, children }) => {
    const enabled = !!paymentMethods[method]?.enabled;
    const isEditing = activeSection === method;

    return (
      <motion.section
        variants={fadeUp}
        initial="initial"
        animate="animate"
        className="border h-full border-neutral-150 dark:border-neutral-900 bg-white dark:bg-neutral-900/30 rounded-xl p-5"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-10 w-10 rounded-xl border border-neutral-150 dark:border-neutral-900 bg-neutral-50 dark:bg-neutral-950/60 flex items-center justify-center shrink-0">
              {icon}
            </div>

            <div className="min-w-0">
              <h2 className="text-sm font-[500] text-neutral-900 dark:text-white">{title}</h2>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                {subtitle || (enabled ? "Payment method active" : "Payment method not set up")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {!isEditing && (
              <SoftButton onClick={() => setActiveSection(method)} className="h-9 px-3 text-xs">
                <PencilSimple size={14} />
                {enabled ? "Edit" : "Set Up"}
              </SoftButton>
            )}

            {!isEditing && (
              <StatusToggle enabled={enabled} onClick={() => requestToggle(method)} disabled={saving} />
            )}

            {isEditing && (
              <SoftButton onClick={() => setActiveSection(null)} className="h-9 w-9 px-0">
                <X size={16} />
              </SoftButton>
            )}
          </div>
        </div>
        

        <AnimatePresence initial={false}>
          {children({ enabled, isEditing })}
        </AnimatePresence>
      </motion.section>
    );
  };

  const BankTransferSection = () => {
    const method = "bankTransfer";
    return Section({
      method,
      title: "Bank Transfer",
      icon: <Buildings size={18} className="text-neutral-700 dark:text-neutral-200" />,
      children: ({ enabled, isEditing }) => (
          <>
            {!isEditing && (
              <motion.div variants={panelAnim} initial="initial" animate="animate" exit="exit" className="mt-5">
                {enabled ? (
                  <div className="bg-neutral-50 dark:bg-neutral-950/40 border border-neutral-150 dark:border-neutral-900 rounded-xl p-4">
                    <div className="space-y-3">
                      {[
                        ["Bank Name", paymentMethods[method].bankName],
                        ["Account Name", paymentMethods[method].accountName],
                        ["Account Number", paymentMethods[method].accountNumber],
                        ["Routing Number", paymentMethods[method].routingNumber],
                        ["SWIFT/BIC", paymentMethods[method].swiftCode],
                        ["Address", paymentMethods[method].address],
                      ].map(([label, value]) => (
                        <div key={label} className="flex items-start justify-between gap-3">
                          <span className="text-xs text-neutral-500 dark:text-neutral-400">{label}</span>
                          <div className="flex items-center gap-2 text-right">
                            <span className="text-sm font-[500] text-neutral-900 dark:text-white break-all">
                              {value || "—"}
                            </span>
                            {value ? (
                              <button
                                onClick={() => copyToClipboard(value)}
                                className="p-2 rounded-xl hover:bg-neutral-200 dark:hover:bg-neutral-800"
                              >
                                <Copy size={14} />
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>

                    {paymentMethods[method].additionalInfo ? (
                      <div className="mt-4 p-3 rounded-xl border border-blue-100 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800/40">
                        <div className="flex items-start gap-2">
                          <Info size={16} className="text-blue-600 dark:text-blue-300 mt-0.5" />
                          <div>
                            <p className="text-sm font-[500] text-blue-700 dark:text-blue-200">Additional Information</p>
                            <p className="text-xs text-blue-600 dark:text-blue-300">
                              {paymentMethods[method].additionalInfo}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <EmptyHint
                    title="No Bank Transfer payment details set up"
                    desc="Add your bank transfer payment details so clients can pay you using this method."
                  />
                )}
              </motion.div>
            )}

            {isEditing && (
              <motion.div variants={panelAnim} initial="initial" animate="animate" exit="exit" className="mt-5">
                <div className="border border-neutral-150 dark:border-neutral-900 bg-white dark:bg-neutral-950/40 rounded-xl p-5">
                  <h3 className="text-sm font-[500] text-neutral-900 dark:text-white mb-4">
                    Bank Transfer Details
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputCustom
                      label="Bank Name"
                      placeholder="Enter your bank name"
                      value={paymentMethods[method].bankName}
                      onChange={(v) => handleInputChange(method, "bankName", v)}
                      required
                      icon={<Buildings size={16} />}
                    />

                    <InputCustom
                      label="Account Name"
                      placeholder="Enter account holder name"
                      value={paymentMethods[method].accountName}
                      onChange={(v) => handleInputChange(method, "accountName", v)}
                      required
                      icon={<User size={16} />}
                    />

                    <InputCustom
                      label="Account Number"
                      placeholder="Enter account number"
                      value={paymentMethods[method].accountNumber}
                      onChange={(v) => handleInputChange(method, "accountNumber", v)}
                      required
                      icon={<Hash size={16} />}
                    />

                    <InputCustom
                      label="Routing Number"
                      placeholder="Enter routing/sort code"
                      value={paymentMethods[method].routingNumber}
                      onChange={(v) => handleInputChange(method, "routingNumber", v)}
                      required
                      icon={<CaretCircleUp size={16} />}
                    />

                    <div className="md:col-span-2">
                      <InputCustom
                        label="SWIFT/BIC Code"
                        placeholder="Enter SWIFT or BIC code"
                        value={paymentMethods[method].swiftCode}
                        onChange={(v) => handleInputChange(method, "swiftCode", v)}
                        required
                        icon={<Hash size={16} />}
                      />
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                        Required for international transfers
                      </p>
                    </div>

                    <InputCustom
                      label="Address"
                      placeholder="Add address"
                      type="text"
                      value={paymentMethods[method].address || ""}
                      onChange={(v) => handleInputChange(method, "address", v)}
                      icon={<MapPinLine size={16} />}
                    />

                    <div className="md:col-span-2">
                      <InputCustom
                        label="Additional Information (Optional)"
                        placeholder="Add any additional payment instructions"
                        type="textarea"
                        value={paymentMethods[method].additionalInfo || ""}
                        onChange={(v) => handleInputChange(method, "additionalInfo", v)}
                        icon={<Info size={16} />}
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-6">
                    <PrimaryButton onClick={() => savePaymentMethod(method)} disabled={saving}>
                      {saving ? <Spinner size={16} className="animate-spin" /> : <Check size={16} />}
                      Save
                    </PrimaryButton>

                    <SoftButton onClick={() => setActiveSection(null)} disabled={saving}>
                      Cancel
                    </SoftButton>
                  </div>
                </div>
              </motion.div>
            )}
          </>
        ),
    });
  };

  const PayoneerSection = () => {
    const method = "payoneer";
    return Section({
      method,
      title: "Payoneer",
      icon: <CurrencyDollar size={18} className="text-neutral-700 dark:text-neutral-200" />,
      children: ({ enabled, isEditing }) => (
          <>
            {!isEditing && (
              <motion.div variants={panelAnim} initial="initial" animate="animate" exit="exit" className="mt-5">
                {enabled ? (
                  <div className="bg-neutral-50 dark:bg-neutral-950/40 border border-neutral-150 dark:border-neutral-900 rounded-xl p-4">
                    <div className="space-y-3">
                      {[
                        ["Account Name", paymentMethods[method].accountName],
                        ["Email", paymentMethods[method].email],
                      ].map(([label, value]) => (
                        <div key={label} className="flex items-start justify-between gap-3">
                          <span className="text-xs text-neutral-500 dark:text-neutral-400">{label}</span>
                          <div className="flex items-center gap-2 text-right">
                            <span className="text-sm font-[500] text-neutral-900 dark:text-white break-all">
                              {value || "—"}
                            </span>
                            {value ? (
                              <button
                                onClick={() => copyToClipboard(value)}
                                className="p-2 rounded-xl hover:bg-neutral-200 dark:hover:bg-neutral-800"
                              >
                                <Copy size={14} />
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>

                    {paymentMethods[method].additionalInfo ? (
                      <div className="mt-4 p-3 rounded-xl border border-blue-100 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800/40">
                        <div className="flex items-start gap-2">
                          <Info size={16} className="text-blue-600 dark:text-blue-300 mt-0.5" />
                          <div>
                            <p className="text-sm font-[500] text-blue-700 dark:text-blue-200">Additional Information</p>
                            <p className="text-xs text-blue-600 dark:text-blue-300">
                              {paymentMethods[method].additionalInfo}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <EmptyHint
                    title="No Payoneer payment details set up"
                    desc="Add your Payoneer payment details so clients can pay you using this method."
                  />
                )}
              </motion.div>
            )}

            {isEditing && (
              <motion.div variants={panelAnim} initial="initial" animate="animate" exit="exit" className="mt-5">
                <div className="border border-neutral-150 dark:border-neutral-900 bg-white dark:bg-neutral-950/40 rounded-xl p-5">
                  <h3 className="text-sm font-[500] text-neutral-900 dark:text-white mb-4">
                    Payoneer Details
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputCustom
                      label="Payoneer Email"
                      placeholder="Enter your Payoneer email address"
                      type="email"
                      value={paymentMethods[method].email}
                      onChange={(v) => handleInputChange(method, "email", v)}
                      required
                      icon={<EnvelopeSimple size={16} />}
                    />

                    <div>
                      <InputCustom
                        label="Account Name"
                        placeholder="Enter account holder name"
                        value={paymentMethods[method].accountName}
                        onChange={(v) => handleInputChange(method, "accountName", v)}
                        required
                        icon={<User size={16} />}
                      />
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                        Name as it appears on your Payoneer account
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <InputCustom
                        label="Additional Information (Optional)"
                        placeholder="Add any additional payment instructions"
                        type="textarea"
                        value={paymentMethods[method].additionalInfo || ""}
                        onChange={(v) => handleInputChange(method, "additionalInfo", v)}
                        icon={<Info size={16} />}
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-6">
                    <PrimaryButton onClick={() => savePaymentMethod(method)} disabled={saving}>
                      {saving ? <Spinner size={16} className="animate-spin" /> : <Check size={16} />}
                      Save
                    </PrimaryButton>
                    <SoftButton onClick={() => setActiveSection(null)} disabled={saving}>
                      Cancel
                    </SoftButton>
                  </div>
                </div>
              </motion.div>
            )}
          </>
        ),
    });
  };

  const CryptoSection = () => {
    const method = "crypto";

    const networkOptionsAll = useMemo(
      () => [
        { id: "ERC20", name: "ERC20 (Ethereum)", value: "ERC20" },
        { id: "BEP20", name: "BEP20 (BSC)", value: "BEP20" },
      ],
      []
    );

    const networkOptionsTrcOnly = useMemo(
      () => [{ id: "TRC20", name: "TRC20 (Tron)", value: "TRC20" }],
      []
    );

    const coins = paymentMethods.crypto?.coins || [];

    return Section({
      method,
      title: "Crypto",
      icon: <Coins size={18} className="text-neutral-700 dark:text-neutral-200" />,
      children: ({ enabled, isEditing }) => (
          <>
            {!isEditing && (
              <motion.div variants={panelAnim} initial="initial" animate="animate" exit="exit" className="mt-5">
                {enabled ? (
                  <div className="bg-neutral-50 dark:bg-neutral-950/40 border border-neutral-150 dark:border-neutral-900 rounded-xl p-4">
                    <div className="space-y-4">
                      {coins.filter((c) => c.enabled).length === 0 ? (
                        <p className="text-xs text-neutral-600 dark:text-neutral-400">
                          No coins enabled yet.
                        </p>
                      ) : (
                        coins
                          .filter((c) => c.enabled)
                          .map((c) => (
                            <div
                              key={c.coin}
                              className="space-y-2 border-b last:border-b-0 border-neutral-200 dark:border-neutral-800 pb-3"
                            >
                              <Row label="Coin" value={c.coin} />
                              <Row label="Network" value={c.network || "TRC20"} />

                              <div className="flex items-start justify-between gap-3">
                                <span className="text-xs text-neutral-500 dark:text-neutral-400">Wallet</span>
                                <div className="flex items-center gap-2 text-right">
                                  <span className="text-sm font-[500] text-neutral-900 dark:text-white break-all">
                                    {c.wallet || "—"}
                                  </span>
                                  {c.wallet ? (
                                    <button
                                      onClick={() => copyToClipboard(c.wallet)}
                                      className="p-2 rounded-xl hover:bg-neutral-200 dark:hover:bg-neutral-800"
                                    >
                                      <Copy size={14} />
                                    </button>
                                  ) : null}
                                </div>
                              </div>

                              {c.additionalInfo ? (
                                <div className="mt-2 p-3 rounded-xl border border-blue-100 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800/40">
                                  <div className="flex items-start gap-2">
                                    <Info size={16} className="text-blue-600 dark:text-blue-300 mt-0.5" />
                                    <div>
                                      <p className="text-sm font-[500] text-blue-700 dark:text-blue-200">
                                        Additional Information
                                      </p>
                                      <p className="text-xs text-blue-600 dark:text-blue-300">{c.additionalInfo}</p>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          ))
                      )}
                    </div>
                  </div>
                ) : (
                  <EmptyHint
                    title="No Crypto payment details set up"
                    desc="Add your crypto payment details so clients can pay you using this method."
                  />
                )}
              </motion.div>
            )}

            {isEditing && (
              <motion.div variants={panelAnim} initial="initial" animate="animate" exit="exit" className="mt-5">
                <div className="border border-neutral-150 dark:border-neutral-900 bg-white dark:bg-neutral-950/40 rounded-xl p-5">
                  <h3 className="text-sm font-[500] text-neutral-900 dark:text-white mb-4">
                    Crypto Details
                  </h3>

                  <div className="space-y-4">
                    {coins.map((c) => {
                      const isUsdtTrc = c.coin === "USDT (TRC20)";
                      const optionsForCoin = isUsdtTrc ? networkOptionsTrcOnly : networkOptionsAll;
                      const currentNetwork = c.network || (isUsdtTrc ? "TRC20" : "TRC20");

                      return (
                        <div
                          key={c.coin}
                          className="border border-neutral-150 dark:border-neutral-900 bg-white dark:bg-neutral-950/40 rounded-xl p-4"
                        >
                          <div className="flex items-center justify-between gap-3 mb-3">
                            <div className="min-w-0">
                              <p className="text-sm font-[500] text-neutral-900 dark:text-white truncate">
                                {c.coin}
                              </p>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                {isUsdtTrc
                                  ? "USDT (TRC20) is locked to TRC20 (Tron)."
                                  : "Choose network + wallet address."}
                              </p>
                            </div>

                            <button
                              onClick={() => toggleCryptoCoin(c.coin)}
                              className={cx(
                                "px-3 py-1.5 text-xs rounded-xl border transition",
                                c.enabled
                                  ? "bg-green-50 text-green-700 border-green-100 dark:bg-green-900/25 dark:text-green-400 dark:border-green-800/60"
                                  : "bg-neutral-100 text-neutral-700 border-neutral-150 dark:bg-neutral-900/40 dark:text-neutral-300 dark:border-neutral-800/70"
                              )}
                            >
                              {c.enabled ? "Enabled" : "Enable"}
                            </button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-[500] text-neutral-700 dark:text-neutral-300 mb-2">
                                Network
                              </label>
                              <SearchableSelect
                                options={optionsForCoin}
                                value={currentNetwork}
                                onChange={(v) =>
                                  setCryptoCoinField(c.coin, "network", isUsdtTrc ? "TRC20" : v)
                                }
                                placeholder="Select network"
                                icon={<Coins size={16} />}
                              />
                              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                                Make sure the wallet matches the selected network.
                              </p>
                            </div>

                            <div>
                              <InputCustom
                                label={`${c.coin} Wallet`}
                                placeholder={`Enter your ${c.coin} wallet address`}
                                value={c.wallet}
                                onChange={(v) => setCryptoCoinField(c.coin, "wallet", v)}
                                required={c.enabled}
                                icon={<At size={16} />}
                              />
                            </div>

                            <div className="md:col-span-2">
                              <InputCustom
                                label="Additional Information (Optional)"
                                placeholder="Add any additional payment instructions"
                                type="textarea"
                                value={c.additionalInfo || ""}
                                onChange={(v) => setCryptoCoinField(c.coin, "additionalInfo", v)}
                                icon={<Info size={16} />}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex flex-wrap gap-2 mt-6">
                    <PrimaryButton onClick={() => savePaymentMethod(method)} disabled={saving}>
                      {saving ? <Spinner size={16} className="animate-spin" /> : <Check size={16} />}
                      Save
                    </PrimaryButton>
                    <SoftButton onClick={() => setActiveSection(null)} disabled={saving}>
                      Cancel
                    </SoftButton>
                  </div>
                </div>
              </motion.div>
            )}
          </>
        ),
    });
  };

  // Credit Card (Stripe) — disabled, not in use
  // const CreditCardSection = () => { ... };

  // PayPal — disabled, not in use
  // const PayPalSection = () => { ... };

  return (
    <div className="bg-white w-full dark:bg-neutral-950 text-neutral-900 dark:text-white">
      <AnimatePresence>
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 flex items-center justify-center z-50"
          >
            <Spinner size={30} className="animate-spin text-neutral-800 dark:text-neutral-300" />
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmToggleModal
        open={confirmOpen}
        target={confirmTarget}
        saving={saving}
        onClose={() => {
          setConfirmOpen(false);
          setConfirmTarget(null);
        }}
        onConfirm={() => togglePaymentMethod(confirmTarget.method, confirmTarget.newStatus)}
      />

      <div className="mx-auto w-full lg:p-7 text-start p-3">
        <motion.div variants={fadeUp} initial="initial" animate="animate" className="mb-6">
          <div className="rounded-xl border border-neutral-150 dark:border-neutral-900 bg-neutral-50 dark:bg-neutral-900/40 p-4">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-xl border border-neutral-150 dark:border-neutral-900 bg-white dark:bg-neutral-950/60 flex items-center justify-center">
                <WarningCircle size={18} className="text-amber-500" />
              </div>
              <div>
                <h3 className="text-sm font-[500] text-neutral-900 dark:text-white mb-1">Important Note</h3>
                <p className="text-xs text-neutral-600 dark:text-neutral-400">
                  These payment details will be visible to all clients. Make sure the information is accurate and up-to-date.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start text-start">
          {BankTransferSection()}
          {CryptoSection()}
          {PayoneerSection()}
          {/* <CreditCardSection /> — Stripe disabled, not in use */}
          {/* <PayPalSection /> — PayPal disabled, not in use */}
        </div>
      </div>
    </div>
  );
};

export default PaymentSettings;